import { DeployEnv } from '@/env/type';
import escapeRegExp from 'lodash/escapeRegExp';

const MAX_API_CODE_LENGTH = 500;
const AUTH_CENTER_PATTERN = /trpc\.tab\.auth_center\.AuthCenter/;
const AUTH0_TOKEN_PATH = '/api/auth/auth0token';
const API_SUCCESS_PATTERN = /("status":\s*"(success|info)"|"status":\s*""|"code":\s*0)/;
const AUTH0_TOKEN_PATTERN = /"token":\s*"[^"]+"/;
// ResizeObserver、微前端主动中断、Axios 拦截、取消请求及预加载失败均为已知噪音；保留 Script error 继续分析。
const NO_REPORT_MESSAGE_PATTERN = /(ResizeObserver loop limit exceeded|ResizeObserver loop completed with undelivered notifications|此报错可以忽略|SiteAxiosInterceptorError|Promise was canceled|Unable to preload)/;

export const MONITORING_ENDPOINT_PATTERNS = [
  /rumt-sg\.com/,
  /tgalileo\.com\/collect/,
  /galileotelemetry\.tencent\.com\/collect/,
];

export interface ApiRetCodeResult {
  isErr: boolean;
  code: string | number;
}

export interface ApiResponseSource {
  readonly status: number;
  readonly responseURL?: string;
  readonly url?: string;
}

export interface TraceApiConfig {
  injectTraceHeader: 'traceparent';
  injectTraceUrls: RegExp[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

// TAM 对超长返回码的上报可能失败，先压缩 JSON 再执行长度限制。
function compactJsonString(value: string): string {
  try {
    const parsed: unknown = JSON.parse(value);
    const compacted = JSON.stringify(parsed);
    return compacted === undefined ? value : compacted;
  } catch {
    return value;
  }
}

function containsMonitoringEndpoint(value: unknown): boolean {
  return typeof value === 'string'
    && MONITORING_ENDPOINT_PATTERNS.some(pattern => pattern.test(value));
}

function shouldReportLog(log: unknown): boolean {
  if (!isRecord(log)) return true;

  const message = typeof log.msg === 'string' ? log.msg : '';
  const responseCode = log.code ?? log.status;

  if (responseCode === 401 && AUTH_CENTER_PATTERN.test(message)) return false;
  if (NO_REPORT_MESSAGE_PATTERN.test(message)) return false;
  if (containsMonitoringEndpoint(message) || containsMonitoringEndpoint(log.url)) return false;

  return true;
}

export function getAegisEnv(deployEnv: DeployEnv): 'production' | 'pre' | 'test' {
  if (deployEnv === DeployEnv.PROD) return 'production';
  if (deployEnv === DeployEnv.PRE) return 'pre';
  // 迁移期约定 local 与 test 共用测试环境，避免本地数据进入生产或预发布。
  return 'test';
}

function normalizeApiResponse(source: ApiResponseSource): { status: number; url: string } {
  return {
    status: source.status,
    url: source.responseURL || source.url || '',
  };
}

export function handleApiResponse(
  data: unknown,
  requestUrl: string,
  source: ApiResponseSource,
): ApiRetCodeResult {
  const response = normalizeApiResponse(source);
  const isHttpError = !(response.status >= 200 && response.status < 300);
  const isAbcApi = /api[.-]/.test(requestUrl) && /abetterchoice[.-]/.test(requestUrl);

  if (!isAbcApi) {
    return { isErr: isHttpError, code: String(response.status) };
  }

  if (isHttpError) {
    // 当前登录流程未统一处理未登录与登录态过期，401 暂不计入接口错误率。
    return { isErr: response.status !== 401, code: response.status };
  }

  if (typeof data === 'string') {
    if (API_SUCCESS_PATTERN.test(data)) {
      return { isErr: false, code: '0' };
    }
    if (response.url.includes(AUTH0_TOKEN_PATH) && AUTH0_TOKEN_PATTERN.test(data)) {
      return { isErr: false, code: '0' };
    }
    return {
      isErr: true,
      code: compactJsonString(data).slice(0, MAX_API_CODE_LENGTH),
    };
  }

  return {
    isErr: true,
    code: String(data).slice(0, MAX_API_CODE_LENGTH),
  };
}

export function filterLegacyRequest(data: unknown): unknown | false {
  if (!isRecord(data) || !isRecord(data.logs)) return data;

  return shouldReportLog(data.logs) ? data : false;
}

export function filterGalileoLogs(logs: unknown[]): unknown[] {
  return logs.filter(shouldReportLog);
}

export function isGalileoTraceEnabled(value: string | boolean | undefined): boolean {
  return value === 'true' || value === true;
}

export function buildTraceApiConfig(enabled: boolean, apiBaseUrl: string): TraceApiConfig | Record<string, never> {
  if (!enabled) return {};

  return {
    injectTraceHeader: 'traceparent',
    injectTraceUrls: [new RegExp(`^${escapeRegExp(apiBaseUrl)}`)],
  };
}
